import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BoxExtendedProps, Image } from '@common-fe/common-fe';

import { Document } from '@/common/types';
import {
  CALCULATE_ZOOM_PX,
  FULL_ROTATE_DEG,
  ROTATE_CORRELATION_INDEX,
  ROTATE_STEP_DEG,
  RotateStepEnum,
} from '@/components/elements/DocumentViewer/DocumentViewer.constants';
import { StyledImageWrap } from '@/components/elements/DocumentViewer/DocumentViewer.styles';
import { useWindowResize } from '@/utils/hooks/useWindowResize';

import SelectFilePage from './SelectFilePage';

interface Props extends BoxExtendedProps {
  zoom: number;
  rotate: number;
  receipt: Document;
  fileId?: string;
  index?: number;
}
const ImageViewer: React.FC<Props> = ({ zoom, fileId, rotate, index, receipt, as, ...props }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
  });
  /**
   @returns number of side rotated to top (describes in RotateStepEnum).
   0 = top
   1 = left
   2 = bottom
   3 = right
   @see RotateStepEnum
   */
  const rotateStep = useMemo(() => {
    const angleRotate =
      rotate > 0 ? rotate % FULL_ROTATE_DEG : FULL_ROTATE_DEG - (Math.abs(rotate) % FULL_ROTATE_DEG);
    return (angleRotate / FULL_ROTATE_DEG / ROTATE_CORRELATION_INDEX) as RotateStepEnum;
  }, [rotate]);

  const imgLoad = useMemo(() => Boolean(imageSize.width && imageSize.height), [imageSize]);
  const onImgLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const { offsetHeight, offsetWidth } = event.currentTarget;
    setImageSize({
      height: offsetHeight,
      width: offsetWidth,
    });
    setIsImageLoaded(true);
  }, []);
  const imgSizeCorrelation = useMemo(() => imageSize.width / imageSize.height, [imageSize]);
  const isZoomed = useMemo(() => zoom !== 0, [zoom]);
  const rotateVertical = useMemo(() => (rotate / ROTATE_STEP_DEG) % 2 !== 0, [rotate]);

  const imgWrapWidth = useMemo(() => {
    if (isZoomed && imgLoad) {
      return imageSize.width + CALCULATE_ZOOM_PX(zoom);
    }
    return imageSize.width;
  }, [zoom, imageSize, isZoomed, imgLoad]);

  /**
   @returns Calculate translation an image to fit a wrapper
   */
  const translateY = useMemo(() => {
    // width > height
    if (imgSizeCorrelation >= 1) {
      if (rotateStep === RotateStepEnum.left) {
        return (imgWrapWidth * imgSizeCorrelation - imgWrapWidth) / 2;
      }
      if (rotateStep === RotateStepEnum.right) {
        return (imgWrapWidth - imgWrapWidth * imgSizeCorrelation) / 2;
      }
    }
    // height > width
    if (imgSizeCorrelation < 1) {
      if (rotateStep === RotateStepEnum.left) {
        return (imgWrapWidth - imgWrapWidth / imgSizeCorrelation) / 2;
      }
      if (rotateStep === RotateStepEnum.right) {
        return (imgWrapWidth / imgSizeCorrelation - imgWrapWidth) / 2;
      }
    }
    return (imageSize.width - imageSize.height) / 2;
  }, [imgSizeCorrelation, rotateStep, imageSize, imgWrapWidth]);

  const [isResizing, setIsResizing] = useState(false);

  const onResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const onResizeEnd = useCallback(() => {
    if (imgRef.current) {
      const { offsetHeight, offsetWidth } = imgRef.current;
      setImageSize({
        height: offsetHeight,
        width: offsetWidth,
      });
    }
    setIsResizing(false);
  }, []);

  useWindowResize({ onResizeStart, onResizeEnd });

  const isVertical = useMemo(() => rotateVertical && isImageLoaded, [rotateVertical, isImageLoaded]);
  const effectiveWidth = useMemo(
    () => (isResizing ? 'auto' : imgWrapWidth || 'auto'),
    [isResizing, imgWrapWidth]
  );
  const effectiveHeight = useMemo(
    () => (isVertical && !isResizing ? imgWrapWidth * imgSizeCorrelation : 'auto'),
    [isVertical, isResizing, imgWrapWidth, imgSizeCorrelation]
  );

  return (
    <SelectFilePage index={index} fileId={receipt?.fileId || fileId}>
      <StyledImageWrap
        pad="spacing2"
        overflow="hidden"
        background="canvas"
        zoom={zoom}
        rotate={rotate}
        rotateStep={rotateStep}
        translateY={translateY}
        rotateVertical={rotateVertical}
        style={{
          height: effectiveHeight,
          width: effectiveWidth,
        }}
        {...props}
      >
        <Image
          ref={imgRef}
          style={{
            ...(rotateVertical && isImageLoaded
              ? {
                  width: imgWrapWidth * imgSizeCorrelation,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
                }
              : {}),
          }}
          src={receipt.blob}
          alt="icon"
          onLoad={onImgLoad}
        />
      </StyledImageWrap>
    </SelectFilePage>
  );
};

export default ImageViewer;
